'use client';
import { ReactPortal } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import {
  HeaderItemLevel,
  HeaderProps,
} from '@/components/atoms/layout/header/HeaderSB';
import MobileHeaderItemSB from '@/components/atoms/layout/header/MobileHeaderItemSB';
import MobileHeaderNavItemSB from '@/components/atoms/layout/header/MobileHeaderNavItemSB';
import { BlockComponentModel } from '@/components/cms/types/blok-component';

import IconChevronLeft from '~/svg/IconChevronLeft.svg';
import { useTranslation } from '@/lib/i18n/i18n.client';

interface MobileHeaderItemListSBProps {
  menuLevel: HeaderItemLevel<any>[];
  level: string;
}

export type MobileHeaderItemListSBExtraProps = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  selectedMenu: string;
  currentIndex: string;
  onMenuSelect: (value: string) => void;
  currentTitle?: React.ReactNode;
  parentTitle?: React.ReactNode;
};

type ReactPortalWithOptionalChildren = Omit<ReactPortal, 'children'> & {
  children?: ReactPortal['children'];
};

export default function MobileHeaderItemListSB({
  block,
  ...rest
}: BlockComponentModel<MobileHeaderItemListSBProps> &
  MobileHeaderItemListSBExtraProps): ReactPortalWithOptionalChildren | null {
  const { menuLevel, level } = block;
  const {
    selectedMenu,
    onMenuSelect,
    containerRef,
    currentIndex,
    currentTitle,
    parentTitle,
  } = rest;

  const { texts } = useTranslation();

  const isFirstLevel = level === '0' && selectedMenu === `level-0-0`;

  function handleMenuClick(index: number) {
    onMenuSelect(`level-${Number(level) + 1}-${currentIndex}-${index}`);
  }

  function handleReturnClick() {
    const removedLastIndex = currentIndex?.split('-').slice(0, -1).join('-');

    onMenuSelect(`level-${Number(level) - 1}-${removedLastIndex}`);
  }

  const isOpen = selectedMenu === `level-${level}-${currentIndex}`;

  if (!containerRef.current) return null;

  function checkIfChildLevelsExistsAndNotEmpty(
    item: HeaderProps['header']['firstLevels'][number] & {
      thirdLevels?: { id: string; level: string; title: React.ReactNode }[];
    }
  ) {
    return (
      ('secondLevels' in item && item.secondLevels?.length === 0) ||
      ('thirdLevels' in item && item.thirdLevels?.length === 0) ||
      (!!!item?.secondLevels && !!!item?.thirdLevels)
    );
  }

  return createPortal(
    <div
      className={cn(
        'flex w-full flex-col gap-8 overflow-auto',
        !isOpen && `hidden`
      )}
    >
      {!isFirstLevel && (
        <Button
          slim
          leftSection={<IconChevronLeft />}
          onClick={() => handleReturnClick()}
          variant='text'
          color='tertiary'
          classNames={{ root: 'font-sans' }}
        >
          {parentTitle || texts.header.main_menu}
        </Button>
      )}

      {currentTitle && (
        <p className='text-label-text-l text-neutral-N40'>{currentTitle}</p>
      )}

      {menuLevel?.map((item, index) => (
        <div key={index}>
          {item?.link &&
          checkIfChildLevelsExistsAndNotEmpty(item as HeaderItemLevel<any>) ? (
            <MobileHeaderNavItemSB
              block={{
                menuLinkItem: item as HeaderItemLevel<any>,
              }}
            />
          ) : (
            <MobileHeaderItemSB
              // eslint-disable-next-line
              block={{ menuLevel: item as any }}
              onClick={() => handleMenuClick(index)}
              containerRef={containerRef}
              selectedMenu={selectedMenu}
              onMenuSelect={onMenuSelect}
              currentIndex={`${currentIndex}-${index}`}
              currentTitle={item.title}
              parentTitle={currentTitle}
            />
          )}
        </div>
      ))}
    </div>,
    containerRef?.current
  ) as ReactPortalWithOptionalChildren;
}
